import { memo, useEffect, useState } from 'react';

import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { yupResolver } from '@hookform/resolvers/yup';
import EditIcon from 'assets/edit.svg';
import Button from 'common-ui/button';
import ColorPicker from 'common-ui/colorPicker';
import Input from 'common-ui/input';
import Modal from 'common-ui/modal';
import { Select } from 'common-ui/select';
import Switch from 'common-ui/switch';
import TextArea from 'common-ui/textArea';
import {
  createWorkflowStatus,
  editWorkflowStatus,
  getWorkflowStatuses,
} from 'features/sales/salesActions';
import * as Yup from 'yup';

import { BtnWrapper, Form, ShiftControl, WorkflowSwitch } from '../Sales.styles';
import useSearchData from './useSearchData';

const StatusActionDefinition = [
  'new',
  'final_status',
  'commission_ready',
  'approval_needed',
  'approved_customer',
  'need_appointment',
  'customer_verification_needed',
  'commission_available',
  'sprint_approved',
  'rejected',
  'closed',
  'done',
];

const statusOptions = StatusActionDefinition.map((status) => ({
  label: status.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()),
  value: status,
}));
const nameRegex = /^[A-Za-zԱ-Ֆա-ֆև0-9 ,.:#\-/'&()]+$/;

const schema = Yup.object().shape({
  name: Yup.string()
    .required('Name is required')
    .matches(
      nameRegex,
      'Only  Latin and Armenian letters, numbers, and allowed special characters'
    ),
  statusColor: Yup.string()
    .matches(/^#([0-9A-F]{3}){1,2}$/i, 'Invalid hex color')
    .required('Status color is required'),
  statusActionDefinition: Yup.string()
    .oneOf(StatusActionDefinition, 'Invalid status action')
    .required('Status action is required'),
});

const colorOptions = [
  '#E63946',
  '#2D6CDF',
  '#28A745',
  '#FFC107',
  '#6F42C1',
  '#FD7E14',
  '#6C757D',
  '#000000',
  '#FFFFFF',
];

const Create = ({ isEdit = false, initialData = null, onCloseEdit }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalWidth, setModalWidth] = useState('430px');
  const { searchData } = useSearchData();

  const dispatch = useDispatch();

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      description: '',
      statusColor: '#FF5733',
      statusActionDefinition: 'new',
    },
    shouldFocusError: false,
  });

  useEffect(() => {
    if (initialData) {
      reset({
        name: initialData.name || '',
        description: initialData.description || '',
        statusColor: initialData.statusColor || '#FF5733',
        statusActionDefinition: initialData.statusActionDefinition || 'new',
        isEnabled: !initialData.isEnabled,
      });
    }
  }, [initialData, reset]);

  const handleOpenModal = () => setIsModalOpen(true);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    reset();
    if (onCloseEdit) onCloseEdit();
  };

  const onSubmit = (data) => {
    const body = {
      name: data.name,
      description: data.description,
      statusColor: data.statusColor,
      statusActionDefinition: data.statusActionDefinition,
      isEnabled: !data.isEnabled,
    };

    const action =
      isEdit && initialData?.uuid
        ? editWorkflowStatus({ uuid: initialData.uuid, body })
        : createWorkflowStatus(body);

    dispatch(action).then(() => {
      dispatch(getWorkflowStatuses(searchData));
      handleCloseModal();
    });
  };

  useEffect(() => {
    const handleResize = () => {
      setModalWidth(window.innerWidth < 500 ? '370px' : '430px');
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      {isEdit ? (
        <ShiftControl onClick={handleOpenModal}>
          <img alt="Edit icon" src={EditIcon} />
        </ShiftControl>
      ) : (
        <BtnWrapper>
          <Button secondary onClick={handleOpenModal} className="h-38">
            + Add Workflow Status
          </Button>
        </BtnWrapper>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        width={modalWidth}
        onOk={handleSubmit(onSubmit)}
        title={isEdit ? 'Edit Workflow Status' : 'Create Workflow Status'}
        footer={true}
      >
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                label="Name"
                error={errors.name?.message}
                placeholder="Enter name"
                maxLength={50}
                required
              />
            )}
          />

          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextArea
                {...field}
                resizeHorizontal={false}
                label="Description"
                error={errors.description?.message}
                placeholder="Enter description"
                maxLength={250}
              />
            )}
          />

          <Controller
            name="statusColor"
            control={control}
            render={({ field }) => (
              <ColorPicker
                label="Select status color"
                colorOptions={colorOptions}
                selectedColor={field.value}
                handleColorClick={(color) => field.onChange(color)}
                onChange={field.onChange}
                error={errors.statusColor?.message}
              />
            )}
          />

          <Controller
            name="statusActionDefinition"
            control={control}
            render={({ field }) => (
              <div style={{ marginTop: '16px' }}>
                <Select
                  {...field}
                  label="Status Action"
                  options={statusOptions}
                  value={statusOptions.find((opt) => opt.value === field.value)}
                  onChange={(selected) => field.onChange(selected?.value)}
                  $error={errors.statusActionDefinition?.message}
                  placeholder="Select status action"
                />
              </div>
            )}
          />

          <Controller
            name="isEnabled"
            control={control}
            render={({ field }) => (
              <WorkflowSwitch>
                <Switch isOn={!field.value} onToggle={() => field.onChange(!field.value)} />
                {field.value ? 'Disabled' : 'Enabled'}
              </WorkflowSwitch>
            )}
          />
        </Form>
      </Modal>
    </>
  );
};

export default memo(Create);
